<template>
  <div
    class="the-navigation"
    @[mouseenter]="pointerEnter"
    @[mouseleave]="pointerLeave"
    @focusin="focusEnter"
    @focusout="focusLeave"
    @keydown.esc="escape"
    @click="closeOnLink"
  >
    <nav
      ref="nav"
      tabindex="-1"
      aria-label="Menu principal"
      :class="{
        'is-mobile': isMobile,
        minimized: compact,
      }"
    >
      <div
        v-if="grouped"
        ref="tools"
        class="the-navigation__tools"
        @focusin="searchFocusIn"
        @focusout="searchFocusOut"
      >
        <label
          v-if="grouped && !compact"
          class="the-navigation__search"
        >
          <i
            class="fas fa-search"
            aria-hidden="true"
          />
          <input
            ref="search"
            v-model="query"
            type="search"
            placeholder="Buscar no menu"
            aria-label="Buscar no menu"
            aria-keyshortcuts="Control+K Meta+K"
            autocomplete="off"
            @focus="fieldFocused = true"
            @blur="fieldFocused = false"
            @keydown="searchKeydown"
            @click="searchesDismissed = false"
          >
          <kbd
            v-if="!query && !isMobile"
            aria-hidden="true"
          >{{ shortcutLabel }}</kbd>
        </label>

        <p
          class="is-sr-only"
          role="status"
        >
          {{ searchesSummary }}
        </p>

        <div
          v-if="searchesOpen"
          ref="searches"
          class="the-navigation__searches"
          role="region"
          aria-label="Buscas recentes"
          @mousedown.prevent
          @keydown="searchesKeydown"
        >
          <header class="the-navigation__panel-header">
            <b>Buscas recentes</b>
            <button
              type="button"
              class="the-navigation__clear"
              @click="clearSearches"
            >
              Limpar
            </button>
          </header>

          <ul class="the-navigation__results">
            <li
              v-for="term in searches"
              :key="term"
              class="the-navigation__search-entry"
            >
              <button
                type="button"
                class="the-navigation__search-term"
                :aria-label="`Buscar ${term}`"
                @click="useSearch(term)"
              >
                <i
                  class="fas fa-search"
                  aria-hidden="true"
                />
                <span>{{ term }}</span>
              </button>
              <button
                type="button"
                class="the-navigation__search-remove"
                :aria-label="`Remover busca ${term}`"
                :title="`Remover busca ${term}`"
                @click="dropSearch(term)"
              >
                <i
                  class="fas fa-times"
                  aria-hidden="true"
                />
              </button>
            </li>
          </ul>
        </div>

        <button
          v-if="grouped && compact"
          type="button"
          class="the-navigation__tool"
          aria-label="Buscar no menu"
          :title="`Buscar no menu (${shortcutLabel})`"
          @click="openSearch"
        >
          <i
            class="fas fa-search"
            aria-hidden="true"
          />
        </button>
      </div>

      <p
        class="is-sr-only"
        role="status"
      >
        {{ resultsSummary }}
      </p>

      <template v-if="results">
        <ul
          v-if="results.length"
          ref="results"
          class="the-navigation__results"
          @keydown="resultsKeydown"
          @click.capture="recordResult"
          @auxclick.capture="recordResult"
          @click="clearOnCurrent"
        >
          <NavigationItem
            v-for="(route, index) in results"
            :key="`result-${index}`"
            :route="route"
            :suggested="fieldFocused && index === 0"
          />
        </ul>

        <p
          v-else
          class="the-navigation__empty"
        >
          {{ emptyResults[0] }}
          <br>
          {{ emptyResults[1] }}
        </p>
      </template>

      <ul v-else>
        <template v-if="shortcutsEnabled">
          <NavigationSection
            v-if="favorites.length"
            title="Favoritos"
            :routes="favorites"
            class="the-navigation__favorites"
          />
          <li class="the-navigation__recents">
            <button
              ref="recentsToggle"
              type="button"
              class="the-navigation__recents-toggle"
              :class="{ 'is-open': recentsOpen }"
              :aria-expanded="String(recentsOpen)"
              aria-controls="navigation-recents"
              @click="toggleRecents"
            >
              <i
                class="fas fa-history"
                aria-hidden="true"
              />
              <span>Recentes</span>
              <i
                class="fas fa-chevron-right"
                aria-hidden="true"
              />
            </button>
          </li>
        </template>

        <template v-if="navigation.sections.length">
          <NavigationSection
            v-for="(section, i) in navigation.sections"
            :key="`section-${i}`"
            :title="section.title"
            :routes="section.items"
            :collapsible="accordion"
            :open="openSections.includes(section.title)"
            :current="section.title === currentSection"
            @toggle="openSections = toggleSection(openSections, section.title)"
          />
        </template>

        <template v-else>
          <NavigationItem
            v-for="(route, index) in navigation.items"
            :key="index"
            :route="route"
          />
        </template>
      </ul>

      <p
        class="is-sr-only"
        role="status"
      >
        {{ favoriteNotice }}
      </p>

      <NavigationRecents
        v-if="recentsOpen"
        ref="recentsPanel"
        :days="recentDays"
        :frame="$el"
        :anchor="$refs.recentsToggle"
        :mobile="isMobile"
        @close="closeRecents"
      />
    </nav>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import { landingPath } from '@/router/middlewares/requireSession';

import NavigationSection from '@/components/Navigation/NavigationSection.vue';
import NavigationItem from '@/components/Navigation/NavigationItem.vue';
import NavigationRecents from '@/components/Navigation/NavigationRecents.vue';
import {
  buildNavigationMenu,
  emptySearchMessage,
  hasGroupedNavigation,
  isMenuSearchShortcut,
  listStep,
  openSectionFor,
  searchAnnouncement,
  searchKeyAction,
  searchNavigation,
  searchShortcutLabel,
  sectionForPath,
  toggleSection,
} from '@/utils/navigationMenu';
import {
  FAVORITES_LIMIT,
  groupRecentsByDay,
  isFavorite,
  navigationOwner,
  readFavorites,
  readRecents,
  readSearches,
  recordSearch,
  removeSearch,
  resolveFavorites,
  resolveRecents,
  resolveSearches,
  toggleFavorite,
  writeFavorites,
  writeSearches,
} from '@/utils/navigationShortcuts';
import { toast } from '@/utils/message';
import { sessionLanding } from '@/utils/promoterAccess';

const withGroup = (item) => ({ ...item, group: item['menu-item'] ?? null });

export default {
  name: 'TheNavigation',
  components: {
    NavigationSection,
    NavigationItem,
    NavigationRecents,
  },
  provide() {
    return {
      navigationFavorites: {
        enabled: () => this.shortcutsEnabled,
        has: (route) => isFavorite(this.favorites, route),
        full: () => this.favorites.length >= FAVORITES_LIMIT,
        toggle: (route) => this.toggleFavorite(route),
      },
    };
  },
  data: () => ({
    favoritesStored: null,
    favoriteNotice: '',
    recentsStored: [],
    recentsOpen: false,
    recentsNow: 0,
    searchesStored: [],
    searchFocused: false,
    fieldFocused: false,
    searchesDismissed: false,
    query: '',
    openSections: [],
    pointerInside: false,
    focusOnExpand: false,
    returnFocus: null,
    placingFocus: false,
  }),
  computed: {
    ...mapState({
      minimized: (state) => state.retract.minimized,
      pinned: (state) => state.retract.pinned,
      userId: (state) => state.user.userInfo.id,
      token: (state) => state.app.token,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      consultScope: 'user/consultScope',
      sessionMenu: 'user/sessionMenu',
    }),
    navigation() {
      return buildNavigationMenu({
        consultPaths: this.consultScope?.paths ?? null,
        routers: this.sessionMenu?.routers,
      });
    },
    owner() {
      return navigationOwner({ userId: this.userId, token: this.token });
    },
    shortcutsEnabled() {
      return this.accordion && Boolean(this.owner);
    },
    landing() {
      return sessionLanding({
        consult: this.consultScope,
        menuDefault: this.sessionMenu?.default ?? landingPath(),
      });
    },
    favorites() {
      return resolveFavorites({
        stored: this.favoritesStored,
        items: this.navigation.items,
        landing: this.landing,
      }).map(withGroup);
    },
    recentDays() {
      const recents = resolveRecents({
        stored: this.recentsStored,
        items: this.navigation.items,
        favorites: this.favorites,
        currentPath: this.$route.path,
      }).map(withGroup);

      return groupRecentsByDay(recents, this.recentsNow);
    },
    searches() {
      if (!this.shortcutsEnabled) return [];

      return resolveSearches({ stored: this.searchesStored, sections: this.navigation.sections });
    },
    searchesOpen() {
      if (!this.searchFocused || this.searchesDismissed || this.query) return false;

      return this.searches.length > 0;
    },
    searchesSummary() {
      if (!this.searchesOpen) return '';

      return this.searches.length === 1 ? '1 busca recente' : `${this.searches.length} buscas recentes`;
    },
    compact() {
      return this.minimized && !this.isMobile;
    },
    currentSection() {
      return sectionForPath(this.navigation.sections, this.$route.path);
    },
    shortcutLabel() {
      return searchShortcutLabel(navigator.userAgentData?.platform ?? navigator.platform);
    },
    grouped() {
      return hasGroupedNavigation(this.navigation.sections);
    },
    accordion() {
      return this.grouped && !this.compact;
    },
    results() {
      return this.accordion ? searchNavigation(this.navigation.sections, this.query) : null;
    },
    emptyResults() {
      return emptySearchMessage(this.query);
    },
    resultsSummary() {
      return searchAnnouncement(this.results, this.query);
    },
    hoverEnabled() {
      return this.pinned && !this.isMobile;
    },
    mouseenter() {
      return this.hoverEnabled ? 'mouseenter' : null;
    },
    mouseleave() {
      return this.hoverEnabled ? 'mouseleave' : null;
    },
  },
  watch: {
    owner: {
      immediate: true,
      handler(owner) {
        this.favoritesStored = readFavorites(owner);
        this.searchesStored = readSearches(owner);
        this.favoriteNotice = '';
        this.recentsOpen = false;
      },
    },
    shortcutsEnabled(enabled) {
      if (!enabled) this.closeRecents(false);
    },
    $route() {
      this.closeRecents(Boolean(this.$refs.recentsPanel?.$el.contains(document.activeElement)));
      this.favoriteNotice = '';

      if (this.query && this.$el.contains(document.activeElement)) this.focusCurrentAfterRender();

      this.query = '';
      this.openSections = openSectionFor(this.openSections, this.currentSection);
      this.$el.scrollTop = 0;
    },
    query() {
      this.searchesDismissed = false;
    },
    minimized() {
      this.$el.scrollTop = 0;
    },
    pinned() {
      this.$el.scrollTop = 0;
    },
    compact(isCompact) {
      if (isCompact || !this.focusOnExpand) return;

      this.focusOnExpand = false;
      this.$nextTick(() => this.$refs.search?.focus());
    },
  },
  created() {
    this.openSections = openSectionFor([], this.currentSection);
  },
  mounted() {
    window.addEventListener('keydown', this.shortcut);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.shortcut);
  },
  methods: {
    toggleSection,
    toggleFavorite(route) {
      const { list, blocked } = toggleFavorite({
        stored: readFavorites(this.owner),
        items: this.navigation.items,
        landing: this.landing,
        item: route,
      });

      if (blocked) {
        const notice = `Você já tem ${FAVORITES_LIMIT} favoritos. Remova um para incluir outro.`;

        this.favoriteNotice = '';
        this.$nextTick(() => { this.favoriteNotice = notice; });
        toast(notice, 'warning');
        return;
      }

      this.favoriteNotice = '';
      this.favoritesStored = list;
      writeFavorites(this.owner, list);
      this.$refs.recentsPanel?.keepFocus();
    },
    toggleRecents() {
      if (this.recentsOpen) this.closeRecents(true);
      else this.openRecents();
    },
    openRecents() {
      this.recentsStored = readRecents(this.owner);
      this.recentsNow = Date.now();
      this.recentsOpen = true;
    },
    closeRecents(restoreFocus) {
      if (!this.recentsOpen) return;

      this.recentsOpen = false;

      if (restoreFocus) this.$nextTick(() => this.$refs.recentsToggle?.focus());
    },
    shortcut(event) {
      if (!this.grouped || !isMenuSearchShortcut(event)) return;

      event.preventDefault();
      this.openSearch();
    },
    openSearch() {
      if (!this.$el.contains(document.activeElement)) this.returnFocus = document.activeElement;

      this.searchesDismissed = false;

      if (!this.compact) {
        this.$refs.search?.focus();
        return;
      }

      this.focusOnExpand = true;
      this.$emit('expand');
    },
    focusCurrentAfterRender() {
      this.$nextTick(() => {
        this.placingFocus = true;
        this.$el.querySelector('a.router-link-exact-active, a.is-active')?.focus();
        this.placingFocus = false;
      });
    },
    leaveSearch() {
      const target = this.returnFocus;

      this.returnFocus = null;

      if (target?.isConnected && target !== document.body) target.focus();
      else if (this.hoverEnabled) this.$refs.search?.blur();
    },
    escape(event) {
      if (!this.isMobile || event.defaultPrevented || this.recentsOpen) return;

      this.$emit('close');
    },
    closeOnLink(event) {
      const plain = event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey;

      if (this.isMobile && plain && event.target.closest('a')) this.$emit('close');
    },
    focusMenu() {
      this.$refs.nav?.focus();
    },
    holdsFocus() {
      const { activeElement } = document;

      if (this.query) return this.$el.contains(activeElement);

      if (activeElement === this.$refs.search) return true;
      if (activeElement === this.$refs.recentsToggle) return true;
      if (this.$refs.recentsPanel?.$el.contains(activeElement)) return true;

      return Boolean(this.$refs.searches?.contains(activeElement));
    },
    pointerEnter() {
      this.pointerInside = true;
      this.$emit('mouseover', false);
    },
    pointerLeave() {
      this.pointerInside = false;

      if (!this.holdsFocus()) this.$emit('mouseover', true);
    },
    focusEnter({ target }) {
      if (this.placingFocus || !this.hoverEnabled) return;

      if (!this.compact) {
        this.$emit('mouseover', false);
        return;
      }

      if (!target.matches(':focus-visible')) return;

      this.focusOnExpand = true;
      this.$emit('expand');
    },
    focusLeave({ relatedTarget }) {
      if (!this.hoverEnabled || this.pointerInside || this.$el.contains(relatedTarget)) return;

      this.$emit('mouseover', true);
    },
    resultLinks() {
      return [...(this.$refs.results?.querySelectorAll('a') ?? [])];
    },
    searchKeydown(event) {
      if (event.isComposing) return;

      const action = searchKeyAction({
        key: event.key,
        query: this.query,
        searchesOpen: this.searchesOpen,
        searchesDismissed: this.searchesDismissed,
        mobile: this.isMobile,
      });

      if (!action || action === 'pass') return;

      if (action === 'open') {
        const [first] = this.resultLinks();

        if (!first) return;

        event.preventDefault();
        first.click();
        return;
      }

      if (action === 'first') {
        const [first] = this.query ? this.resultLinks() : this.searchTerms();

        if (!first) return;

        event.preventDefault();
        first.focus();
        return;
      }

      event.preventDefault();

      if (action === 'clear') this.query = '';
      else if (action === 'dismiss') this.searchesDismissed = true;
      else if (action === 'leave') this.leaveSearch();
      else {
        this.searchesDismissed = false;
        this.$nextTick(() => this.searchTerms()[0]?.focus());
      }
    },
    searchTerms() {
      return [...(this.$refs.searches?.querySelectorAll('.the-navigation__search-term') ?? [])];
    },
    searchFocusIn({ target }) {
      if (target === this.$refs.search && !this.searchFocused) {
        this.searchesStored = readSearches(this.owner);
        this.searchesDismissed = false;
      }

      this.searchFocused = true;
    },
    searchFocusOut({ relatedTarget }) {
      if (this.$refs.tools?.contains(relatedTarget)) return;

      this.searchFocused = false;
    },
    recordResult({ target, button }) {
      if (target.closest('a') && button <= 1) recordSearch(this.owner, this.query, this.navigation.sections);
    },
    clearOnCurrent({ target }) {
      if (target.closest('a')?.pathname !== this.$route.path) return;

      this.query = '';
      this.focusCurrentAfterRender();
    },
    useSearch(term) {
      this.query = term;
      this.$refs.search?.focus();
    },
    dropSearch(term) {
      const list = removeSearch(readSearches(this.owner), term);

      if (this.$refs.searches?.contains(document.activeElement)) {
        const terms = this.searchTerms();
        const index = this.searches.indexOf(term);

        (terms[index + 1] ?? terms[index - 1] ?? this.$refs.search)?.focus();
      }

      writeSearches(this.owner, list);
      this.searchesStored = list;
    },
    clearSearches() {
      writeSearches(this.owner, []);
      this.searchesStored = [];
      this.$refs.search?.focus();
    },
    searchesKeydown(event) {
      const terms = this.searchTerms();
      const row = document.activeElement?.closest('li');
      const index = terms.findIndex((term) => row?.contains(term));

      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        this.searchesDismissed = true;
        this.$refs.search?.focus();
        return;
      }

      this.stepThrough(event, terms, index);
    },
    resultsKeydown(event) {
      const links = this.resultLinks();
      const index = links.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        event.preventDefault();
        this.query = '';
        this.$refs.search?.focus();
        return;
      }

      this.stepThrough(event, links, index);
    },
    stepThrough(event, list, index) {
      const step = listStep({ key: event.key, index, length: list.length });

      if (step === null) return;

      event.preventDefault();

      if (step === 'field') this.$refs.search?.focus();
      else list[step]?.focus();
    },
  },
};
</script>

<style lang="scss">
.the-navigation {
  @import '@/assets/scss/src/_textVariables.scss';
  @import '@/assets/scss/src/_fieldSignature.scss';

  %navigation-floating {
    @include floating-surface;

    z-index: 101;

    padding: 0.5rem;
  }

  %navigation-control {
    @include ghost-control;
  }

  %navigation-key {
    padding: 0 0.375rem;
    border: 1px solid $gray-300;
    border-radius: $radius-control;

    font: {
      family: $font-family;
      size: 10px;
    }
    line-height: 1.125rem;
    color: $gray-500;

    background-color: $gray-50;
    pointer-events: none;
  }

  nav {
    padding: 1rem 0.5rem;
    font-size: 12px;

    &.minimized {
      a {
        justify-content: center;
      }
    }

    > ul > li {
      margin-bottom: 0;
    }

    ul {
      display: grid;
    }

    li {
      margin-bottom: 0.25rem;

      > ul {
        margin-bottom: 1rem;
      }
    }

    a {
      position: relative;

      display: flex;
      padding: 0.5rem 1rem;
      border-radius: $radius-control;
      justify-content: flex-start;

      font: {
        family: $font-family;
        weight: 500;
        size: 0.75rem;
      }
      color: $color-secondary;

      align-items: center;

      &.is-suggested {
        background-color: $gray-100;
      }

      &:hover,
      &.router-link-exact-active,
      &.router-link-active,
      &.is-active {
        background: {
          color: $color-secondary;
          blend-mode: multiply;
        }
        color: $color-light;
      }

      @include focus-ring;
    }

    i {
      font-size: 1.25rem;
    }

    span {
      margin-left: 16px !important;

      font: {
        size: 12px;
        weight: inherit;
      }
      line-height: 150%;
    }

    .menu-label {
      color: $color-secondary;

      &.is-collapsible {
        @extend %navigation-control;

        display: flex;
        width: 100%;
        margin-bottom: 0.25rem;
        padding: 0.5rem;
        align-items: center;
        gap: 0.5rem;

        font: {
          family: $font-family;
          size: 12px;
        }
        text-transform: uppercase;
        letter-spacing: 0.05em;

        i {
          font-size: 0.75rem;
        }
      }

      &__title {
        flex: 1;
        text-align: left;
        font-weight: 700;
      }

      &__current {
        width: 0.375rem;
        height: 0.375rem;
        margin-left: 0 !important;
        border-radius: 50%;

        background-color: $color-secondary;
      }

      &__count {
        min-width: 1.5rem;
        padding: 0 0.375rem;
        border-radius: 999px;

        font-size: 10px;
        line-height: 1.125rem;
        text-align: center;

        background-color: $gray-200;
      }
    }

    .navigation-link__group {
      font-size: 12px;
      opacity: 0.7;
    }

    .chip {
      border-radius: 10px;
      padding: 2px 10px;

      font: {
        family: Inter;
        size: 10px;
        weight: 500;
      }
      text-align: center;

      background-color: #DBEAFE;
      color: #1E40AF;
    }

    .the-navigation__tools {
      position: relative;

      display: flex;
      margin-bottom: 1rem;
      align-items: center;
      gap: 0.5rem;
    }

    .the-navigation__searches {
      @extend %navigation-floating;

      position: absolute;
      top: calc(100% + 0.25rem);
      right: 0;
      left: 0;

      li {
        margin-bottom: 0;
      }
    }

    .the-navigation__clear {
      @extend %navigation-control;

      min-height: $target-pointer;
      padding: 0.25rem 0.5rem;

      font: {
        family: $font-family;
        size: 0.75rem;
        weight: 500;
      }
      text-transform: none;
      letter-spacing: normal;
    }

    .the-navigation__search-entry {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .the-navigation__search-term {
      @extend %navigation-control;

      display: flex;
      min-width: 0;
      padding: 0.5rem;
      flex: 1;
      align-items: center;

      font: {
        family: $font-family;
        weight: 500;
        size: 0.75rem;
      }
      text-align: left;

      i {
        color: $color-secondary-500;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .the-navigation__search-remove {
      @extend %navigation-control;

      color: $color-secondary-500;

      &:hover,
      &:focus-visible {
        color: $color-secondary;
      }

      display: flex;
      width: 2rem;
      height: 2rem;
      padding: 0;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;

      i {
        font-size: 0.875rem;
      }
    }

    label.the-navigation__search {
      position: relative;
      flex: 1;
      min-width: 0;

      i {
        position: absolute;
        top: 50%;
        left: 0.75rem;
        z-index: 1;

        transform: translateY(-50%);

        font-size: 0.75rem;
        color: $color-secondary-500;
      }

      input {
        @include field-frame;
        @include field-text;
        @include field-focus;

        width: 100%;
        padding: 0 0.75rem 0 2.25rem;

        font: {
          family: $font-family;
          size: 12px;
        }
      }
    }

    &:not(.is-mobile) .the-navigation__search input {
      padding-right: 3.25rem;
    }

    .the-navigation__search kbd {
      @extend %navigation-key;

      position: absolute;
      top: 50%;
      right: 0.5rem;

      transform: translateY(-50%);
    }

    .the-navigation__tool {
      @extend %navigation-control;

      display: flex;
      width: $target-pointer;
      height: $target-pointer;
      padding: 0;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;

      i {
        font-size: 1rem;
      }
    }

    .the-navigation__results {
      grid-template-columns: minmax(0, 1fr);
    }

    .the-navigation__results a,
    .the-navigation__favorites a {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      align-items: center;

      i {
        grid-row: span 2;
      }

      > span:first-of-type {
        grid-column: 2 / span 2;
      }

      .navigation-link__group {
        grid-column: 2;
        margin-left: 16px;
        padding-left: 0;
        line-height: 1.2;
      }

      .chip {
        grid-row: 3;
        grid-column: 1;
      }
    }

    .the-navigation__empty {
      @include empty-state;
    }

    .navigation-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;

      > a {
        flex: 1;
        min-width: 0;
      }

      &:hover,
      &:focus-within {
        .navigation-item__star {
          opacity: 1;
        }
      }
    }

    .navigation-link__key {
      @extend %navigation-key;

      grid-column: 3;
      grid-row: 2;
      margin-left: 0.5rem;
      line-height: 0.75rem;
    }

    &.is-mobile .navigation-link__key {
      display: none;
    }

    .navigation-item__star {
      @extend %navigation-control;

      display: flex;
      width: 2rem;
      height: 2rem;
      padding: 0;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;

      opacity: 0;

      @media (hover: none) {
        opacity: 1;
      }

      i {
        font-size: 0.875rem;
      }

      &.is-favorite {
        opacity: 1;
      }

      &[aria-disabled='true'] {
        cursor: not-allowed;
      }
    }

    &.is-mobile .navigation-item__star {
      opacity: 1;
    }

    .the-navigation__favorites > .menu-label {
      display: block;
      margin-left: 0 !important;
      padding: 0.5rem;

      font: {
        family: $font-family;
        size: 12px;
        weight: 700;
      }
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .the-navigation__recents {
      margin-bottom: 1rem;
    }

    .the-navigation__recents-toggle {
      @extend %navigation-control;

      display: flex;
      width: 100%;
      padding: 0.5rem 1rem;
      align-items: center;

      font: {
        family: $font-family;
        weight: 500;
        size: 0.75rem;
      }
      text-align: left;

      .fa-chevron-right {
        margin-left: auto;
        font-size: 0.75rem;
      }

      &.is-open {
        background-color: $gray-100;
      }
    }

    .the-navigation__panel {
      @extend %navigation-floating;

      position: fixed;

      display: flex;
      flex-direction: column;
      overflow-y: auto;
    }

    .the-navigation__panel-header {
      display: flex;
      padding: 0 0 0.5rem 0.5rem;
      align-items: center;
      justify-content: space-between;

      font: {
        family: $font-family;
        size: 12px;
      }
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $color-secondary;
    }

    .the-navigation__panel-label {
      padding: 0.5rem 0.5rem 0.25rem;

      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $gray-500;
    }

    &.is-mobile {
      padding: 1rem;

      a {
        padding: 0.5rem;
      }

      a,
      .menu-label.is-collapsible,
      .the-navigation__recents-toggle,
      .the-navigation__search-term {
        min-height: $target-touch;
      }

      i {
        font-size: 1rem;
      }

      .the-navigation__tool {
        width: $target-touch;
        height: $target-touch;
      }

      .navigation-item__star,
      .the-navigation__search-remove {
        width: $target-touch;
        height: $target-touch;
        padding: 0.375rem;

        background-clip: content-box;
      }
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &.minimized {
      span:not(.menu-label):not(.chip) {
        display: none;
      }

      .menu-label {
        margin: 0 !important;

        display: inline-block;
        width: 100%;

        text-align: center;
        font-size: .625rem;
      }

      .chip {
        position: absolute;
        bottom: -10px;
        left: 0;
        margin-left: 0 !important;

        font-size: 7.5px;
      }

      .the-navigation__tools {
        flex-direction: column;
      }
    }
  }
}
</style>
